import { Box, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useState } from 'react';
import { useCountyApi } from '../../services/api/useCounty.api';
import { BuilderCatalogRef } from '../../types/SimilarlySituated/BuilderCatalogRef.type';
import { CountyBuilderCatalogType } from '../../types/SimilarlySituated/CountyBuilderCatalogType.type';
import { SsbCharacteristic } from '../../types/SimilarlySituated/SsbCharacteristic.type';
import { SsbEntryPoint } from '../../types/enums/SsbEntryPoint.enum';
import { BuilderCatalogFlag } from '../../types/enums/BuilderCatalogFlag.enum';
import { findCharacteristic } from '../../utils/ssbCatalogMetadata';
import { entryPointColors } from '../../utils/ssbConditionColors';
import StateBadge from './StateBadge';
import SsbSectionLabel from './SsbSectionLabel';

interface AvailabilityCatalogProps {
  entryPoint: SsbEntryPoint;
  countyId?: number | null;
  onSelect: (characteristic: SsbCharacteristic) => void;
}

const ENTRY_POINT_FILTER: Record<SsbEntryPoint, { [key: string]: boolean }> = {
  inclusion: { show_in_inclusion: true },
  except: { show_in_except: true },
  'box-wide': { show_in_exclude: true },
};

interface CatalogRow {
  typeId: number;
  label: string;
  flag?: BuilderCatalogRef;
  characteristic?: SsbCharacteristic;
}

interface CatalogGroupView {
  id: number;
  label: string;
  rows: CatalogRow[];
}

interface CatalogSectionView {
  id: number;
  label: string;
  groups: CatalogGroupView[];
}

function bySeedOrder(a: { id: number }, b: { id: number }): number {
  return b.id - a.id;
}

function buildSections(rows: CountyBuilderCatalogType[]): CatalogSectionView[] {
  const sections = new Map<number, CatalogSectionView>();
  const groups = new Map<number, CatalogGroupView>();

  rows.forEach((row) => {
    const type = row.builder_catalog_type;
    const group = type.builder_catalog_group;
    const section = group.builder_catalog_section;

    if (!sections.has(section.id)) {
      sections.set(section.id, { id: section.id, label: section.name, groups: [] });
    }
    if (!groups.has(group.id)) {
      const groupView: CatalogGroupView = { id: group.id, label: group.name, rows: [] };
      groups.set(group.id, groupView);
      sections.get(section.id)?.groups.push(groupView);
    }

    groups.get(group.id)?.rows.push({
      typeId: type.id,
      label: type.name,
      flag: row.builder_catalog_flag,
      characteristic: findCharacteristic(type.id),
    });
  });

  const ordered = [...sections.values()].sort(bySeedOrder);
  ordered.forEach((section) => {
    section.groups.sort(bySeedOrder);
    section.groups.forEach((group) => group.rows.sort((a, b) => b.typeId - a.typeId));
  });

  return ordered;
}

// selectable = correct dataset and editor exists
function isSelectable(row: CatalogRow): boolean {
  if (!row.characteristic) return false;
  return row.flag?.id === BuilderCatalogFlag.AVAILABLE || row.flag?.id === BuilderCatalogFlag.PARTIAL;
}

// the catalog for selected county
function AvailabilityCatalog({ entryPoint, countyId, onSelect }: AvailabilityCatalogProps) {
  const { getCountyBuilderCatalogTypes } = useCountyApi();
  const [expandedGroups, setExpandedGroups] = useState<number[]>([]);
  const colors = entryPointColors(entryPoint);

  const {
    data: catalogRows,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['countyBuilderCatalogTypes', countyId, entryPoint],
    queryFn: () => getCountyBuilderCatalogTypes(countyId as number, ENTRY_POINT_FILTER[entryPoint]),
    enabled: countyId !== null && countyId !== undefined,
  });

  const toggleGroup = (groupId: number) => {
    setExpandedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
  };

  if (countyId === null || countyId === undefined || isError || (!isPending && (catalogRows?.length ?? 0) === 0)) {
    return (
      <Box sx={{ padding: '12px 16px 16px' }}>
        <Typography sx={{ fontSize: '13px', color: '#718096' }}>
          The availability catalog could not be loaded for this dataset.
        </Typography>
      </Box>
    );
  }

  if (isPending) {
    return (
      <Box sx={{ padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} variant='rounded' height={38} sx={{ borderRadius: '6px' }} />
        ))}
      </Box>
    );
  }

  const renderRow = (row: CatalogRow) => {
    const selectable = isSelectable(row);

    return (
      <Box
        key={row.typeId}
        component='button'
        type='button'
        disabled={!selectable}
        onClick={() => row.characteristic && onSelect(row.characteristic)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          width: '100%',
          border: 'none',
          borderRadius: '6px',
          backgroundColor: 'transparent',
          padding: '7px 10px 7px 34px',
          textAlign: 'left',
          cursor: selectable ? 'pointer' : 'default',
          '&:hover': { backgroundColor: selectable ? colors.background : 'transparent' },
        }}
      >
        <Typography sx={{ fontSize: '14px', color: selectable ? '#2D3748' : '#A0AEC0', minWidth: 0 }}>
          {row.label}
        </Typography>
        <StateBadge flag={row.flag} />
      </Box>
    );
  };

  const renderGroup = (group: CatalogGroupView) => {
    const isExpanded = expandedGroups.includes(group.id);

    return (
      <Box key={group.id} sx={{ marginBottom: '6px' }}>
        <Box
          component='button'
          type='button'
          onClick={() => toggleGroup(group.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            width: '100%',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            backgroundColor: '#fff',
            padding: '9px 12px',
            textAlign: 'left',
            cursor: 'pointer',
            '&:hover': { borderColor: colors.border },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            {isExpanded ? (
              <ChevronDownIcon size={14} color='#4A5568' />
            ) : (
              <ChevronRightIcon size={14} color='#4A5568' />
            )}
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1A202C' }}>{group.label}</Typography>
          </Box>
        </Box>
        {isExpanded && group.rows.map(renderRow)}
      </Box>
    );
  };

  return (
    <Box sx={{ padding: '4px 16px 16px' }}>
      {buildSections(catalogRows).map((section) => (
        <Box key={section.id}>
          <SsbSectionLabel sx={{ padding: '10px 2px 6px' }}>{section.label}</SsbSectionLabel>
          {section.groups.map(renderGroup)}
        </Box>
      ))}
    </Box>
  );
}

export default AvailabilityCatalog;
